import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

function emptyRange(index = 0) {
  return {
    id: `rango-${Date.now()}-${index}`,
    label: `Rango ${index + 1}`,
    start: '08:00',
    end: '16:00',
    overnight: false,
  };
}

function emptySet(index = 0) {
  return {
    id: `conjunto-${Date.now()}-${index}`,
    name: `Conjunto ${index + 1}`,
    description: '',
    ranges: [emptyRange(0)],
  };
}

const TimeRangeSetsAdmin = ({ open, getAuthHeaders, embedded = false }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sets, setSets] = useState([]);

  const loadSetsMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/admin/time-range-sets', { headers }),
  });

  const loadSets = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setLoading(true);
    try {
      const res = await loadSetsMutation.mutateAsync(headers);
      if (res.data.success) setSets(res.data.sets || []);
    } catch (err) {
      showError(err.response?.data?.error || 'Error al cargar rangos horarios');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, showError]);

  useEffect(() => {
    if (open) loadSets();
  }, [open, loadSets]);

  const patchSet = (setIndex, patch) => {
    setSets((prev) => prev.map((s, i) => (i === setIndex ? { ...s, ...patch } : s)));
  };

  const patchRange = (setIndex, rangeIndex, patch) => {
    setSets((prev) =>
      prev.map((s, si) =>
        si !== setIndex
          ? s
          : {
              ...s,
              ranges: (s.ranges || []).map((r, ri) => (ri === rangeIndex ? { ...r, ...patch } : r)),
            }
      )
    );
  };

  const addSet = () => setSets((prev) => [...prev, emptySet(prev.length)]);

  const removeSet = (setIndex) => {
    setSets((prev) => prev.filter((_, i) => i !== setIndex));
  };

  const addRange = (setIndex) => {
    setSets((prev) =>
      prev.map((s, i) =>
        i !== setIndex ? s : { ...s, ranges: [...(s.ranges || []), emptyRange(s.ranges?.length || 0)] }
      )
    );
  };

  const removeRange = (setIndex, rangeIndex) => {
    setSets((prev) =>
      prev.map((s, i) =>
        i !== setIndex ? s : { ...s, ranges: (s.ranges || []).filter((_, ri) => ri !== rangeIndex) }
      )
    );
  };

  const saveSetsMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.put('/api/admin/time-range-sets', body, { headers }),
  });

  const handleSave = async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setSaving(true);
    try {
      const res = await saveSetsMutation.mutateAsync({ body: { sets }, headers });
      if (res.data.success) {
        setSets(res.data.sets || []);
        showSuccess('Rangos horarios guardados');
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('¿Restaurar los conjuntos predeterminados (turnos, franjas, diurno/nocturno)?')) return;
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setSaving(true);
    try {
      const res = await saveSetsMutation.mutateAsync({ body: { sets: [] }, headers });
      if (res.data.success) {
        setSets(res.data.sets || []);
        showSuccess('Conjuntos predeterminados restaurados');
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Error al restaurar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: embedded ? 1 : 0 }}>
      <Alert severity="info" sx={{ py: 0.5 }}>
        Define rangos horarios reutilizables (turnos, mañana/tarde/noche, diurno/nocturno, etc.). Se aplican a
        cualquier columna <strong>fecha/hora</strong> del modelo como filtro en el editor de gráficos. La hora se
        evalúa en la zona horaria del dashboard.
      </Alert>

      {sets.map((set, setIndex) => (
        <Accordion key={set.id || setIndex} defaultExpanded={setIndex === 0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{set.name || 'Sin nombre'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({(set.ranges || []).length} rangos)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <TextField
                size="small"
                label="Nombre del conjunto"
                value={set.name || ''}
                onChange={(e) => patchSet(setIndex, { name: e.target.value })}
                sx={{ minWidth: 220, flex: 1 }}
              />
              <TextField
                size="small"
                label="Descripción"
                value={set.description || ''}
                onChange={(e) => patchSet(setIndex, { description: e.target.value })}
                sx={{ minWidth: 260, flex: 2 }}
              />
              <IconButton color="error" onClick={() => removeSet(setIndex)} title="Eliminar conjunto">
                <DeleteOutlineIcon />
              </IconButton>
            </Box>

            {(set.ranges || []).map((range, rangeIndex) => (
              <Paper key={range.id || rangeIndex} variant="outlined" sx={{ p: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Etiqueta"
                  value={range.label || ''}
                  onChange={(e) => patchRange(setIndex, rangeIndex, { label: e.target.value })}
                  sx={{ minWidth: 160, flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Desde"
                  type="time"
                  value={range.start || '08:00'}
                  onChange={(e) => patchRange(setIndex, rangeIndex, { start: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Hasta (excl.)"
                  type="time"
                  value={range.end || '16:00'}
                  onChange={(e) => patchRange(setIndex, rangeIndex, { end: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(range.overnight)}
                      onChange={(e) => patchRange(setIndex, rangeIndex, { overnight: e.target.checked })}
                    />
                  }
                  label="Cruza medianoche"
                />
                <IconButton size="small" color="error" onClick={() => removeRange(setIndex, rangeIndex)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}

            <Button size="small" startIcon={<AddIcon />} onClick={() => addRange(setIndex)}>
              Añadir rango
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<AddIcon />} onClick={addSet}>
          Nuevo conjunto
        </Button>
        <Button startIcon={<RestoreIcon />} onClick={handleRestoreDefaults} disabled={saving}>
          Restaurar predeterminados
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar rangos horarios'}
        </Button>
      </Box>
    </Box>
  );
};

export default TimeRangeSetsAdmin;
